import { useEffect, useMemo, useState } from 'react'

import { WorkflowQuickStudyFinding, WorkflowRun } from '@digital-twins/shared-types'

import { Result } from '@/components/quick-study-results/quickStudyTypes'
import { StudyDocQuote } from '@/components/study-doc'

import { collectRunStudyDocBlocks } from './composeRunStudyDoc'
import {
  composeThreadStudyDoc,
  plotIdForFinding,
  ThreadStudyStrip,
} from './composeThreadStudyDoc'
import { toRenderableResult } from './ThreadRunResults'
import { fetchRunCached } from './useRunResults'

/**
 * Exhibit lookups for a REPORT body, resolved across the whole thread rather
 * than against one run (Step 8, D11).
 *
 * `plots` and `strips` are keyed by `question_id` — the id a report addresses
 * a finding by, `{{plot:<question_id>}}` / `{{quotes_strip:<question_id>}}`,
 * and the id the writer sees in its material's `# Findings (<id>)` headers.
 * `quotes` keeps the fact-bundle exhibit keys `composeThreadStudyDoc` stamps
 * (`quote:<question name>:<n>` plus its unprefixed alias), because that is
 * what both `{{quote:…}}` and the magazine band's own quote lookup address.
 *
 * No `populationId` and no `decisionConfidence`. Those are single-valued and
 * un-keyed: a thread with two twin tests has two of each, and handing a
 * report either one would put one study's audience or one study's P(best)
 * under claims drawn from both. Portrait tiles fall back to initials, and the
 * masthead is simply absent, which is the honest reading.
 */
export interface ThreadExhibits {
  plots: Record<string, Result>
  quotes: Record<string, StudyDocQuote>
  strips: Record<string, ThreadStudyStrip>
}

export interface ComposeThreadExhibitsOptions {
  /**
   * Run token of the artifact being read, when it has lineage of its own.
   *
   * Its findings claim their ids outright and can never be poisoned by a
   * collision: a document produced BY a run demonstrably drew on that run,
   * which is the same "key on the artifact" rule the per-run study-doc path
   * already follows. Absent for an assistant-written report, which has no
   * producing run at all.
   */
  preferRunToken?: string | null
}

/**
 * The id a `{{plot:<id>}}` / `{{quotes_strip:<id>}}` token addresses this
 * finding by.
 *
 * Mirrors `resolveQuestionId` in the backend's
 * `assistant-results-knowledge.service.ts` (and the private
 * `questionIdFromFinding` in `thread-artifacts-promote.service.ts`, which
 * stamps the `# Findings (<id>)` header the report writer reads): the
 * FactBundle's own id wins once a living extend has run over this finding,
 * and `study_doc_plot_id` — stamped by python's study-doc pipeline on every
 * completed run — is the id everything else carries.
 */
export function questionIdFromFinding(
  finding: WorkflowQuickStudyFinding,
): string | undefined {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return undefined
  }
  const row = payload as Record<string, unknown>
  const bundle = row.fact_bundle
  if (bundle && typeof bundle === 'object' && !Array.isArray(bundle)) {
    const raw = (bundle as Record<string, unknown>).question_id
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim()
    }
  }
  const fallback = row.study_doc_plot_id
  if (typeof fallback === 'string' && fallback.trim()) {
    return fallback.trim()
  }
  return undefined
}

/**
 * A keyed lookup that refuses to guess.
 *
 * The first claimant of a key owns it. A SECOND claimant does not overwrite
 * it and does not lose to it: the key is retired, its value deleted, and no
 * later claim can revive it. Order of iteration therefore cannot change the
 * outcome, which is the whole point — "first wins" and "last wins" are both
 * arbitrary picks wearing a determinism costume.
 *
 * Keys the PINNED run touched are settled by that run alone — it is processed
 * first, and every key it claimed (or retired against itself) is then closed
 * to the rest of the thread. Pinning answers "which run", never "which of two
 * findings in that run": a duplicate inside the pinned run retires the key
 * exactly like any other collision.
 */
interface ExhibitClaims<T> {
  values: Record<string, T>
  pinned: Set<string>
  claimed: Set<string>
  retired: Set<string>
}

function emptyClaims<T>(): ExhibitClaims<T> {
  return {
    values: {},
    pinned: new Set<string>(),
    claimed: new Set<string>(),
    retired: new Set<string>(),
  }
}

function claimExhibit<T>(
  store: ExhibitClaims<T>,
  key: string,
  value: T,
  isPinned: boolean,
): void {
  if (!key) {
    return
  }
  if (isPinned) {
    /* Recorded whatever the outcome, so the rest of the thread cannot claim
       a key the artifact's own run already settled. */
    store.pinned.add(key)
    if (store.retired.has(key)) {
      return
    }
    if (store.claimed.has(key)) {
      store.retired.add(key)
      delete store.values[key]
      return
    }
    store.claimed.add(key)
    store.values[key] = value
    return
  }
  if (store.pinned.has(key) || store.retired.has(key)) {
    return
  }
  if (store.claimed.has(key)) {
    store.retired.add(key)
    delete store.values[key]
    return
  }
  store.claimed.add(key)
  store.values[key] = value
}

function questionNameFromResult(result: Result | undefined): string {
  const name = result?.question?.name
  return typeof name === 'string' ? name.trim() : ''
}

function stripForQuestion(
  questionId: string,
  result: Result | undefined,
): ThreadStudyStrip {
  const questionName = questionNameFromResult(result)
  return questionName ? { id: questionId, questionId: questionName } : { id: questionId }
}

/**
 * Exhibit lookups keyed by `question_id` across every COMPLETED twin-test run
 * in a thread (Step 8, D11).
 *
 * A report synthesises across runs, so keying its exhibits to one run the way
 * a study doc does would leave most of its tokens unresolved. Widening the
 * source, though, widens the id space onto a key that was never thread-unique:
 * `question_id` is `q1`, `q2`, … PER RUN (python stamps
 * `study_doc_plot_id = qid` inside one quick study), so two twin tests in one
 * thread collide on `q1` as the rule, not as an edge case.
 *
 * Nothing in a report body says which run a given sentence came from — the
 * token is a bare `{{plot:q1}}`, and an assistant-written report has no
 * `sourceRunToken` — so for a colliding id there is no run to resolve to.
 * Such an id is dropped from every map and the body renders its existing
 * `Plot: <id>` placeholder. The one exception is `preferRunToken`: when the
 * document itself carries lineage, that run's findings are pinned and win
 * outright. This keeps the invariant the per-artifact keying was written for
 * — a document with no plots is honest, a document showing another study's
 * plots is not — on a key that cannot carry it alone.
 *
 * Runs are deduped by token, and anything not `completed` is skipped: a
 * running or failed run has no findings a report was allowed to cite.
 * Unknown ids are simply absent, never an error.
 */
export function composeThreadExhibits(
  runs: Array<WorkflowRun | null | undefined>,
  options?: ComposeThreadExhibitsOptions,
): ThreadExhibits {
  const preferRunToken = options?.preferRunToken?.trim() || null
  const plots = emptyClaims<Result>()
  const quotes = emptyClaims<StudyDocQuote>()
  const strips = emptyClaims<ThreadStudyStrip>()

  /* Pinned run first so every key it touches is settled before the rest of
     the thread is read. Otherwise the outcome would depend on where the
     document's own run happened to sit in the caller's list. */
  const ordered =
    preferRunToken === null
      ? runs
      : [
          ...runs.filter((run) => run?.token === preferRunToken),
          ...runs.filter((run) => run?.token !== preferRunToken),
        ]

  const seenRuns = new Set<string>()
  for (const run of ordered) {
    if (!run || run.status !== 'completed' || seenRuns.has(run.token)) {
      continue
    }
    seenRuns.add(run.token)

    /* The template is not fetched per run here: it only feeds `populationId`,
       which this composition deliberately drops. `collectRunStudyDocBlocks`
       falls back to the `audience` input key, and an unused token costs
       nothing. */
    const collected = collectRunStudyDocBlocks(run, undefined)
    if (!collected) {
      continue
    }
    const isPinned = preferRunToken !== null && run.token === preferRunToken
    /* One pass through the run's OWN composition, so results carry the same
       `exhibitSpec` stamping, quote enrichment and lookup aliases a study doc
       from this run would get. Only the keys change. */
    const parts = composeThreadStudyDoc(collected.blocks, toRenderableResult)

    Object.entries(parts.quotes).forEach(([key, quote]) => {
      claimExhibit(quotes, key, quote, isPinned)
    })

    for (const block of collected.blocks) {
      block.findings.forEach((finding, position) => {
        const questionId = questionIdFromFinding(finding)
        if (!questionId) {
          return
        }
        const result = parts.plots[plotIdForFinding(finding, block.stepIndex, position)]
        if (result) {
          claimExhibit(plots, questionId, result, isPinned)
        }
        claimExhibit(strips, questionId, stripForQuestion(questionId, result), isPinned)
      })
    }
  }

  return { plots: plots.values, quotes: quotes.values, strips: strips.values }
}

/**
 * Every run behind a list of run tokens, fetched lazily and shared.
 *
 * `useRunResults` reads ONE token per hook call, which a thread-wide read
 * cannot use: the token list has no fixed length. This is the same read
 * through the same module cache, driven by one effect over the whole list, so
 * a run the page or a study-doc card already loaded costs nothing here.
 *
 * Pass an empty list to fetch nothing. The caller decides when thread-wide
 * lookups are needed at all (a report body carrying embed tokens); a thread
 * nobody opened a report in must never fan out reads for its whole history.
 *
 * A run that cannot be read is simply absent from the result: its exhibits
 * fall back to placeholders, which is what an unreadable run honestly means.
 */
export function useThreadExhibitRuns(
  runTokens: Array<string>,
): Array<WorkflowRun> {
  const [runsByToken, setRunsByToken] = useState<Record<string, WorkflowRun>>({})

  /* Joined, not the array itself: the caller's memo hands back a fresh array
     identity on every living-artifact refresh, and a dep on that would refetch
     the thread's whole run history each time. */
  const tokenKey = runTokens.join(',')

  useEffect(() => {
    const tokens = tokenKey ? tokenKey.split(',') : []
    if (tokens.length === 0) {
      return
    }
    let cancelled = false
    tokens.forEach((runToken) => {
      fetchRunCached(runToken)
        .then((next) => {
          if (cancelled) {
            return
          }
          setRunsByToken((prev) =>
            prev[runToken] === next ? prev : { ...prev, [runToken]: next },
          )
        })
        .catch(() => {
          /* Swallowed on purpose. See the doc comment: an unreadable run
             contributes no exhibits rather than an error state, because the
             document around it still reads. */
        })
    })

    return () => {
      cancelled = true
    }
  }, [tokenKey])

  return useMemo(() => {
    const tokens = tokenKey ? tokenKey.split(',') : []
    const resolved: Array<WorkflowRun> = []
    for (const runToken of tokens) {
      const next = runsByToken[runToken]
      if (next) {
        resolved.push(next)
      }
    }
    return resolved
  }, [tokenKey, runsByToken])
}
